import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {ResizeObserver | undefined} */
  #productGridBannerResizeObserver;

  /** @type {MutationObserver | undefined} */
  #productGridBannerMutationObserver;

  /** @type {number | undefined} */
  #productGridBannerAnimationFrame;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#observeProductGridBannerMetrics();
    this.#observeProductGridBannerChildren();
    this.#queueProductGridBannerMetricsSync();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#productGridBannerResizeObserver?.disconnect();
    this.#productGridBannerMutationObserver?.disconnect();

    if (this.#productGridBannerAnimationFrame) {
      cancelAnimationFrame(this.#productGridBannerAnimationFrame);
      this.#productGridBannerAnimationFrame = undefined;
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#queueProductGridBannerMetricsSync();
  }

  #observeProductGridBannerMetrics() {
    this.#productGridBannerResizeObserver?.disconnect();

    const { grid } = this.refs;
    if (!grid) return;

    // Observe the grid itself: banner sizing depends only on the grid's (stable,
    // viewport-bound) width, never on a card's rendered height. Observing a card
    // gallery instead would create a feedback loop — the gallery stretches to the
    // banner-inflated row, which would feed an ever-larger height back into the
    // banners and run the grid away to infinity (especially on mobile).
    this.#productGridBannerResizeObserver = new ResizeObserver(this.#queueProductGridBannerMetricsSync);
    this.#productGridBannerResizeObserver.observe(grid);
  }

  #observeProductGridBannerChildren() {
    this.#productGridBannerMutationObserver?.disconnect();

    const { grid } = this.refs;
    if (!grid) return;

    // Re-sync when the grid's children change (e.g. infinite scroll appends a page,
    // or a banner is added/removed) so newly rendered banners get their height.
    this.#productGridBannerMutationObserver = new MutationObserver(this.#queueProductGridBannerMetricsSync);

    this.#productGridBannerMutationObserver.observe(grid, {
      childList: true,
    });
  }

  #queueProductGridBannerMetricsSync = () => {
    if (this.#productGridBannerAnimationFrame) return;

    this.#productGridBannerAnimationFrame = requestAnimationFrame(() => {
      this.#productGridBannerAnimationFrame = undefined;
      this.#syncProductGridBannerMetrics();
    });
  };

  #syncProductGridBannerMetrics() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;

    // Derive the banner media height from the grid's intrinsic column geometry
    // rather than measuring a product card. The column count is layout-stable and
    // the grid's own width is viewport-bound, so this value cannot be inflated by
    // the banner heights it sets — breaking the runaway-growth feedback loop.
    const gridWidth = grid.clientWidth;
    if (!gridWidth) return;

    const styles = getComputedStyle(grid);
    const columnCount = styles.gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    const columnGap = Number.parseFloat(styles.columnGap) || 0;
    const rowGap = Number.parseFloat(styles.rowGap || styles.gap || '0') || 0;
    const columnWidth = (gridWidth - columnGap * (columnCount - 1)) / columnCount;
    if (columnWidth <= 0) return;

    // Match a real product card's image aspect ratio so a "card" banner lines up
    // with the product images beside it. Fall back to the banner's own 4 / 5.
    const ratio = this.#productCardMediaRatio(grid) ?? 0.8;
    const mediaHeight = columnWidth / ratio;
    if (!Number.isFinite(mediaHeight) || mediaHeight <= 0) return;

    const ratioValue = `${ratio}`;
    if (grid.style.getPropertyValue('--product-grid-card-media-ratio') !== ratioValue) {
      grid.style.setProperty('--product-grid-card-media-ratio', ratioValue);
    }

    const heights = {
      '--product-grid-card-media-height': mediaHeight,
      '--product-grid-banner-jumbo-height': mediaHeight * 2 + rowGap,
      '--product-grid-banner-mega-height': mediaHeight * 4 + rowGap * 3,
    };
    for (const [property, height] of Object.entries(heights)) {
      const value = `${height}px`;
      if (grid.style.getPropertyValue(property) !== value) {
        grid.style.setProperty(property, value);
      }
    }

    this.#syncJumboBannerBottomOffsets(grid);
  }

  /**
   * Aligns each Jumbo or Mega banner's bottom edge with the media of the items
   * closing its bottom grid row. Mega retains the Jumbo class for fallback sizing
   * and shares this alignment measurement when expanded to four rows.
   *
   * A jumbo spans two rows and the fill-banner-gaps stylesheet stretches it to
   * 100% of that area, i.e. to the bottom of the second row's tallest item. Next
   * to product cards that includes their text block, so the banner overshoots
   * the imagery beside it. Measure how far those items' media sits above the
   * track bottom and publish the distance per banner for the stylesheet to
   * subtract. A row closed by another banner (all media, no text) measures 0,
   * so a jumbo beside a wide banner keeps its full height.
   *
   * Loop-safe: row heights are driven by the cards, never by the jumbo (it only
   * follows its grid area), so shrinking the banner cannot feed back into the
   * offsets measured on the next pass.
   *
   * @param {HTMLElement} grid
   */
  #syncJumboBannerBottomOffsets(grid) {
    const jumbos = grid.querySelectorAll('.product-grid__item--banner-jumbo');
    if (jumbos.length === 0) return;

    const items = grid.querySelectorAll('.product-grid__item');

    for (const jumbo of jumbos) {
      if (!(jumbo instanceof HTMLElement)) continue;

      const jumboRect = jumbo.getBoundingClientRect();
      let offset;

      for (const item of items) {
        if (item === jumbo) continue;

        const itemRect = item.getBoundingClientRect();
        // Grid items stretch to their track, so only items in the jumbo's bottom
        // row share its bottom edge.
        if (Math.abs(itemRect.bottom - jumboRect.bottom) > 1) continue;

        const media = item.querySelector('.product-media-container, .product-grid-banner__media');
        if (!(media instanceof Element)) continue;

        const mediaRect = media.getBoundingClientRect();
        if (mediaRect.height <= 0) continue;

        const itemOffset = itemRect.bottom - mediaRect.bottom;
        if (itemOffset < 0 || itemOffset >= jumboRect.height) continue;

        // Align with the lowest media edge in the row.
        if (offset === undefined || itemOffset < offset) offset = itemOffset;
      }

      const nextOffset = `${offset ?? 0}px`;
      if (jumbo.style.getPropertyValue('--product-grid-jumbo-bottom-offset') !== nextOffset) {
        jumbo.style.setProperty('--product-grid-jumbo-bottom-offset', nextOffset);
      }
    }
  }

  /**
   * Reads the rendered product card media aspect ratio (width / height).
   *
   * @param {HTMLElement} grid
   * @returns {number | undefined}
   */
  #productCardMediaRatio(grid) {
    const media = grid.querySelector('.product-grid__item:not(.product-grid__item--banner) .product-media-container');
    if (!(media instanceof HTMLElement)) return undefined;

    const aspect = getComputedStyle(media).aspectRatio;
    if (!aspect || aspect === 'auto') return undefined;

    const [width, height = '1'] = aspect.split('/');
    const w = Number.parseFloat(width);
    const h = Number.parseFloat(height);
    if (!w || !h) return undefined;

    return w / h;
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
